import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { matrimonyApi } from '../matrimonyApi';
import { useAuth } from '../AuthContext';

function ResetPwdModal({ user: target, onClose }) {
  const [pwd, setPwd]     = useState({ newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr]     = useState('');
  const [ok, setOk]       = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(''); setOk(false);
    if (pwd.newPassword.length < 6) { setErr('Password must be at least 6 characters'); return; }
    if (pwd.newPassword !== pwd.confirmPassword) { setErr('Passwords do not match'); return; }
    setSaving(true);
    try {
      await matrimonyApi.adminResetPassword(target.id, pwd.newPassword);
      setOk(true);
      setPwd({ newPassword: '', confirmPassword: '' });
    } catch (e) {
      setErr(e.message || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="m-modal-overlay" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="m-card" onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 420, margin: 16 }}>
        <div className="m-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="m-card-title">🔒 Reset Password</div>
          <button className="m-btn m-btn-ghost m-btn-sm" onClick={onClose} style={{ fontSize: 18, lineHeight: 1, padding: '2px 8px' }}>×</button>
        </div>
        <div className="m-card-body">
          <div style={{ marginBottom: 14, fontSize: 14, color: 'var(--m-muted)' }}>
            Reset password for <strong style={{ color: 'var(--m-text)' }}>{target.name}</strong> ({target.email})
          </div>
          {err && <div className="m-alert m-alert-error" style={{ marginBottom: 12 }}>{err}</div>}
          {ok  && <div className="m-alert m-alert-success" style={{ marginBottom: 12 }}>Password reset successfully! ✓</div>}
          <form onSubmit={handleSubmit}>
            <div className="m-form-group">
              <label className="m-form-label">New Password <span style={{ fontWeight: 400, color: 'var(--m-muted)', fontSize: 12 }}>(min 6 characters)</span></label>
              <input className="m-form-control" type="password" placeholder="New password"
                value={pwd.newPassword}
                onChange={e => { setPwd(p => ({ ...p, newPassword: e.target.value })); setErr(''); setOk(false); }} />
            </div>
            <div className="m-form-group">
              <label className="m-form-label">Confirm Password</label>
              <input className="m-form-control" type="password" placeholder="Repeat new password"
                value={pwd.confirmPassword}
                onChange={e => { setPwd(p => ({ ...p, confirmPassword: e.target.value })); setErr(''); setOk(false); }} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button type="button" className="m-btn m-btn-ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
              <button type="submit" className="m-btn m-btn-primary" style={{ flex: 1 }} disabled={saving}>
                {saving ? 'Resetting…' : 'Reset Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function initials(name) {
  return (name || 'AB').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function StatusBadge({ status }) {
  const cls = status === 'approved' ? 'm-badge-success'
    : status === 'rejected' ? 'm-badge-error'
    : 'm-badge-warning';
  const icon = status === 'approved' ? '✅' : status === 'rejected' ? '❌' : '⏳';
  return <span className={`m-badge ${cls}`}>{icon} {status}</span>;
}

export default function MatrimonyAdmin() {
  const { isAdmin, user } = useAuth();
  const [activeTab, setActiveTab]   = useState('pending');
  const [stats, setStats]           = useState(null);
  const [users, setUsers]           = useState([]);
  const [profiles, setProfiles]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [actionMsg, setActionMsg]   = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [resetTarget, setResetTarget] = useState(null);

  const loadStats = useCallback(() => {
    matrimonyApi.adminStats()
      .then(setStats)
      .catch(e => {
        if (e.message.includes('authorized') || e.message.includes('403')) setAccessDenied(true);
      });
  }, []);

  const loadUsers = useCallback((status = '') => {
    setLoading(true);
    matrimonyApi.adminUsers(status)
      .then(setUsers)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadProfiles = useCallback(() => {
    setLoading(true);
    matrimonyApi.adminProfiles()
      .then(setProfiles)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (activeTab === 'pending') loadUsers('pending');
    else if (activeTab === 'users') loadUsers(userFilter);
    else if (activeTab === 'profiles') loadProfiles();
  }, [activeTab, userFilter, loadUsers, loadProfiles]);

  const setStatus = async (userId, status) => {
    try {
      await matrimonyApi.adminSetStatus(userId, status);
      setActionMsg(`User ${status} successfully`);
      setTimeout(() => setActionMsg(''), 3000);
      loadStats();
      if (activeTab === 'pending') loadUsers('pending');
      else loadUsers(userFilter);
    } catch (e) {
      setActionMsg(`Error: ${e.message}`);
    }
  };

  const removeProfile = async (profileId) => {
    if (!window.confirm('Remove this profile?')) return;
    try {
      await matrimonyApi.adminRemoveProfile(profileId);
      setActionMsg('Profile removed');
      setTimeout(() => setActionMsg(''), 3000);
      loadProfiles();
      loadStats();
    } catch (e) {
      setActionMsg(`Error: ${e.message}`);
    }
  };

  if (accessDenied || (!isAdmin && user !== undefined && user !== null)) {
    return (
      <div className="m-container">
        <div className="m-empty-state">
          <span className="m-empty-state-icon">🔒</span>
          <h3>Admin Access Required</h3>
          <p>You need admin privileges to access this page.</p>
          <Link to="/login" className="m-btn m-btn-primary">Login as Admin</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="m-container">
      <div style={{ marginBottom: 24 }}>
        <h1 className="m-section-title">⚙️ Admin Panel</h1>
        <p style={{ color: 'var(--m-muted)', fontSize: 14 }}>Manage matrimony profiles and user approvals</p>
      </div>

      {actionMsg && (
        <div className={`m-alert ${actionMsg.startsWith('Error') ? 'm-alert-error' : 'm-alert-success'}`}>
          {actionMsg}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="m-stats-grid">
          <div className="m-stat-card">
            <div className="m-stat-icon">👥</div>
            <div className="m-stat-value">{stats.totalUsers ?? 0}</div>
            <div className="m-stat-label">Total Registrations</div>
          </div>
          <div className="m-stat-card">
            <div className="m-stat-icon" style={{ background: 'var(--m-warning-bg)' }}>⏳</div>
            <div className="m-stat-value" style={{ color: 'var(--m-warning)' }}>{stats.pendingUsers ?? 0}</div>
            <div className="m-stat-label">Pending Approval</div>
          </div>
          <div className="m-stat-card">
            <div className="m-stat-icon" style={{ background: 'var(--m-success-bg)' }}>✅</div>
            <div className="m-stat-value" style={{ color: 'var(--m-success)' }}>{stats.approvedUsers ?? 0}</div>
            <div className="m-stat-label">Approved</div>
          </div>
          <div className="m-stat-card">
            <div className="m-stat-icon" style={{ background: 'var(--m-error-bg)' }}>❌</div>
            <div className="m-stat-value" style={{ color: 'var(--m-error)' }}>{stats.rejectedUsers ?? 0}</div>
            <div className="m-stat-label">Rejected</div>
          </div>
          <div className="m-stat-card">
            <div className="m-stat-icon gold">👰</div>
            <div className="m-stat-value" style={{ color: 'var(--m-primary)' }}>{stats.brideProfiles ?? 0}</div>
            <div className="m-stat-label">Bride Profiles</div>
          </div>
          <div className="m-stat-card">
            <div className="m-stat-icon gold">🤵</div>
            <div className="m-stat-value" style={{ color: '#1a56db' }}>{stats.groomProfiles ?? 0}</div>
            <div className="m-stat-label">Groom Profiles</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="m-tabs">
        <button className={`m-tab${activeTab === 'pending' ? ' active' : ''}`} onClick={() => setActiveTab('pending')}>
          ⏳ Pending Approvals {stats?.pendingUsers > 0 && `(${stats.pendingUsers})`}
        </button>
        <button className={`m-tab${activeTab === 'users' ? ' active' : ''}`} onClick={() => setActiveTab('users')}>
          👥 All Users
        </button>
        <button className={`m-tab${activeTab === 'profiles' ? ' active' : ''}`} onClick={() => setActiveTab('profiles')}>
          📋 All Profiles
        </button>
      </div>

      {/* Pending tab */}
      {activeTab === 'pending' && (
        <div>
          {loading ? (
            <div className="m-loading"><div className="m-spinner" /><span>Loading…</span></div>
          ) : users.length === 0 ? (
            <div className="m-empty-state">
              <span className="m-empty-state-icon">🎉</span>
              <h3>No Pending Approvals</h3>
              <p>All registrations have been reviewed.</p>
            </div>
          ) : (
            <div className="m-table-wrap">
              <table className="m-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Gender</th>
                    <th>Age</th>
                    <th>Location</th>
                    <th>Religion</th>
                    <th>Registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td><strong>{u.name}</strong></td>
                      <td style={{ fontSize: 13 }}>{u.email}</td>
                      <td>{u.phone || '—'}</td>
                      <td>{u.gender ? <span className={`m-badge m-badge-${u.gender}`}>{u.gender === 'bride' ? '👰' : '🤵'} {u.gender}</span> : '—'}</td>
                      <td>{u.age || '—'}</td>
                      <td>{[u.city, u.state].filter(Boolean).join(', ') || '—'}</td>
                      <td>{u.religion || '—'}</td>
                      <td style={{ fontSize: 12 }}>{u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN') : '—'}</td>
                      <td>
                        <div className="m-btn-group" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button className="m-btn m-btn-success m-btn-sm" onClick={() => setStatus(u.id, 'approved')}>
                            ✅ Approve
                          </button>
                          <button className="m-btn m-btn-danger m-btn-sm" onClick={() => setStatus(u.id, 'rejected')}>
                            ❌ Reject
                          </button>
                          <button className="m-btn m-btn-ghost m-btn-sm" onClick={() => setResetTarget(u)} title="Reset password">
                            🔒 Reset Pwd
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* All Users tab */}
      {activeTab === 'users' && (
        <div>
          <div className="m-chips-row">
            {['', 'pending', 'approved', 'rejected'].map(s => (
              <button
                key={s}
                className={`m-chip${userFilter === s ? ' active' : ''}`}
                onClick={() => setUserFilter(s)}
              >
                {s === '' ? 'All' : s === 'pending' ? '⏳ Pending' : s === 'approved' ? '✅ Approved' : '❌ Rejected'}
              </button>
            ))}
          </div>
          {loading ? (
            <div className="m-loading"><div className="m-spinner" /><span>Loading…</span></div>
          ) : users.length === 0 ? (
            <div className="m-empty-state">
              <span className="m-empty-state-icon">👥</span>
              <h3>No Users Found</h3>
            </div>
          ) : (
            <div className="m-table-wrap">
              <table className="m-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Gender</th>
                    <th>Age</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th>Registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className={u.status}>
                      <td><strong>{u.name}</strong></td>
                      <td style={{ fontSize: 13 }}>{u.email}</td>
                      <td>{u.gender ? <span className={`m-badge m-badge-${u.gender}`}>{u.gender === 'bride' ? '👰' : '🤵'} {u.gender}</span> : '—'}</td>
                      <td>{u.age || '—'}</td>
                      <td>{[u.city, u.state].filter(Boolean).join(', ') || '—'}</td>
                      <td><StatusBadge status={u.status} /></td>
                      <td style={{ fontSize: 12 }}>{u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN') : '—'}</td>
                      <td>
                        <div className="m-btn-group" style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                          {u.status !== 'approved' && (
                            <button className="m-btn m-btn-success m-btn-sm" onClick={() => setStatus(u.id, 'approved')}>✅</button>
                          )}
                          {u.status !== 'rejected' && (
                            <button className="m-btn m-btn-danger m-btn-sm" onClick={() => setStatus(u.id, 'rejected')}>❌</button>
                          )}
                          {u.status !== 'pending' && (
                            <button className="m-btn m-btn-ghost m-btn-sm" onClick={() => setStatus(u.id, 'pending')}>⏳</button>
                          )}
                          <button className="m-btn m-btn-ghost m-btn-sm" onClick={() => setResetTarget(u)} title="Reset password">
                            🔒
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {resetTarget && (
        <ResetPwdModal user={resetTarget} onClose={() => setResetTarget(null)} />
      )}

      {/* All Profiles tab */}
      {activeTab === 'profiles' && (
        <div>
          {loading ? (
            <div className="m-loading"><div className="m-spinner" /><span>Loading…</span></div>
          ) : profiles.length === 0 ? (
            <div className="m-empty-state">
              <span className="m-empty-state-icon">📋</span>
              <h3>No Profiles Found</h3>
            </div>
          ) : (
            <div className="m-table-wrap">
              <table className="m-table">
                <thead>
                  <tr>
                    <th>Profile</th>
                    <th>Gender</th>
                    <th>Age</th>
                    <th>Location</th>
                    <th>Religion</th>
                    <th>Occupation</th>
                    <th>User Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.map(p => (
                    <tr key={p.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div className="m-avatar" style={{ width: 36, height: 36, fontSize: 12 }}>
                            {p.profile_photo
                              ? <img src={p.profile_photo} alt={p.full_name} />
                              : initials(p.full_name)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{p.full_name || 'No name'}</div>
                            <div style={{ fontSize: 12, color: 'var(--m-muted)' }}>{p.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>{p.gender ? <span className={`m-badge m-badge-${p.gender}`}>{p.gender === 'bride' ? '👰' : '🤵'} {p.gender}</span> : '—'}</td>
                      <td>{p.age || '—'}</td>
                      <td>{[p.city, p.state].filter(Boolean).join(', ') || '—'}</td>
                      <td>{p.religion || '—'}</td>
                      <td>{p.occupation || '—'}</td>
                      <td><StatusBadge status={p.user_status || 'pending'} /></td>
                      <td style={{ fontSize: 12 }}>{p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : '—'}</td>
                      <td>
                        <button className="m-btn m-btn-danger m-btn-sm" onClick={() => removeProfile(p.id)}>
                          🗑️ Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
